import { supabase } from './supabase';

/* =============================================================================
 * The second factor
 * -----------------------------------------------------------------------------
 * Every secret here belongs to Supabase Auth. It generates the TOTP seed,
 * stores it in auth.mfa_factors, checks the six digits against it, and refuses
 * to raise a session above aal1 until one has matched — and a secret this
 * application never holds is a secret this application cannot leak.
 *
 * What this file is for is the shape of the thing, which the raw API leaves
 * scattered: a factor can be unverified, a challenge has to be created before
 * it can be answered, and "does this session need to step up" is a different
 * question from "does this account have a factor".
 *
 * =============================================================================
 * THE TWO LEVELS, AND WHY THE DIFFERENCE MATTERS
 * =============================================================================
 *   currentLevel   what this session has proved
 *   nextLevel      what it could prove if asked
 *
 * aal1 / aal2 means an account with a verified factor on a session that has not
 * used it — which is exactly the state to stop at the door. aal1 / aal1 is an
 * account with no factor at all, and aal2 / aal2 is somebody already through.
 * Reading only the first would let everybody straight in; reading only the
 * second would ask people with no factor for a code they cannot produce.
 * ========================================================================== */

/* =============================================================================
 * Saying what went wrong
 * -----------------------------------------------------------------------------
 * Auth errors do not always arrive as an Error with a sentence in it. A refusal
 * from the server can come back as a bare object, and `error.message` on one of
 * those is undefined — which reached the screen as the two characters `{}` and
 * told somebody trying to protect their account precisely nothing.
 *
 * What comes out of here is either the server's own words or one of two
 * sentinels. The words are left as they are: they are English, they come from
 * Supabase rather than from this product, and rewriting a provider error into
 * something friendlier is how somebody spends an afternoon on a problem the
 * original sentence named in full.
 *
 * The sentinels are for the two cases where there are no words worth showing,
 * and they are translated by the screen — which is where a dictionary is.
 * ========================================================================== */

/** No reason came back at all. Almost always TOTP switched off for the project. */
export const MFA_NO_REASON = 'mfa/no-reason';

/** The server said something, and it reads like the factor type being off. */
export const MFA_LIKELY_DISABLED = 'mfa/likely-disabled';

/**
 * Auth answered with a 5xx, or the request never arrived.
 *
 * Kept apart from the other two because it is not a refusal at all and the
 * advice is opposite. `AuthRetryableFetchError` with an HTTP 500 means the
 * enrolment was never judged — telling somebody to go and switch on a setting
 * they have already switched on sends them round a loop that cannot end.
 */
export const MFA_SERVER_FAULT = 'mfa/server-fault';

function describeAuthError(caught: unknown): string {
  const raw = caught as {
    message?: unknown;
    error_description?: unknown;
    msg?: unknown;
    code?: unknown;
    status?: unknown;
    name?: unknown;
  };

  const usable = (part: unknown): string => (
    typeof part === 'string'
      && part.trim().length > 0
      && part.trim() !== '{}'
      && part.trim() !== '[object Object]'
      ? part.trim()
      : ''
  );

  /* `msg` as well as `message`. GoTrue answers some refusals with one and some
     with the other, and reading only the first is how a real explanation
     arrived at the screen as two curly brackets. */
  /* Judged before anything is read out of it.
     -------------------------------------------------------------------------
     A 5xx, or a fetch that never completed, is the server failing rather than
     the server saying no — and auth-js names it as such: AuthRetryableFetchError
     is the class it raises for exactly this. Reading its (absent) message and
     concluding "the factor type must be switched off" is how somebody ends up
     turning on a setting that was already on, twice. */
  const status = typeof raw?.status === 'number' ? raw.status : 0;
  const retryable = usable(raw?.name) === 'AuthRetryableFetchError' || status >= 500;

  const text = [raw?.message, raw?.error_description, raw?.msg].map(usable).find(Boolean);

  if (retryable) {
    const clues = [
      status > 0 && `HTTP ${status}`,
      usable(raw?.name),
      text,
    ].filter(Boolean);

    return clues.length > 0
      ? `${MFA_SERVER_FAULT}: ${clues.join(', ')}`
      : MFA_SERVER_FAULT;
  }

  if (text) {
    /* Prefixed rather than replaced. The server's sentence is kept — it is the
       only thing that distinguishes one refusal from another — and the screen
       puts a translated explanation in front of it. */
    return /not enabled|disabled|unsupported|mfa_/i.test(text)
      ? `${MFA_LIKELY_DISABLED}: ${text}`
      : text;
  }

  /* No sentence anywhere. Rather than give up, hand over whatever the object
     did carry: a code and a status are enough to look a refusal up, and
     "mfa_factor_name_conflict" or "422" is a great deal more than nothing.

     This is the case that produced `{}`, and the reason it produced it was
     this function insisting on prose that was never there. */
  const clues = [
    usable(raw?.code) && `code ${usable(raw.code)}`,
    typeof raw?.status === 'number' && raw.status > 0 && `HTTP ${raw.status}`,
    usable(raw?.name) && usable(raw.name),
  ].filter(Boolean);

  return clues.length > 0
    ? `${MFA_NO_REASON}: ${clues.join(', ')}`
    : MFA_NO_REASON;
}

export interface Factor {
  id: string;
  friendlyName: string | null;
  verified: boolean;
}

export interface MfaState {
  /** Verified factors. An unverified one is an enrolment somebody abandoned. */
  factors: Factor[];
  /** True when this session still owes a code before it can do anything. */
  mustVerify: boolean;
  /** True when this session has already proved a second factor. */
  verified: boolean;
}

export async function readMfa(): Promise<MfaState> {
  const [{ data: levels }, { data: list }] = await Promise.all([
    supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
    supabase.auth.mfa.listFactors(),
  ]);

  /* listFactors() returns `totp` already filtered to the verified ones, and
     `all` with the abandoned enrolments in it. The screen wants the first; the
     cleanup below wants the second. */
  const factors: Factor[] = (list?.totp ?? []).map((factor) => ({
    id: factor.id,
    friendlyName: factor.friendly_name ?? null,
    verified: factor.status === 'verified',
  }));

  return {
    factors,
    mustVerify: levels?.currentLevel === 'aal1' && levels?.nextLevel === 'aal2',
    verified: levels?.currentLevel === 'aal2',
  };
}

export interface Enrolment {
  factorId: string;
  /**
   * The QR, as an SVG data URI, drawn by Auth itself.
   *
   * No library. A QR encoder is a few hundred lines of Reed–Solomon that would
   * have to be right, and the one thing it would encode is already here.
   */
  qrCode: string;
  /** otpauth:// URI, for a camera that is already pointed somewhere else. */
  uri: string;
  /** The same seed in letters, for somebody typing it in by hand. */
  secret: string;
}

/**
 * Starts an enrolment and hands back what the authenticator app needs.
 *
 * Nothing is in force until verify() has accepted one code. An enrolment that
 * is abandoned here leaves an unverified factor behind, which is why this
 * clears them first — otherwise a second attempt fails on a name collision
 * caused by the first attempt somebody gave up on.
 */
export async function beginEnrolment(friendlyName: string): Promise<Enrolment> {
  const { data: existing } = await supabase.auth.mfa.listFactors();

  for (const factor of existing?.all ?? []) {
    if (factor.status !== 'verified') {
      await supabase.auth.mfa.unenroll({ factorId: factor.id });
    }
  }

  /* Tried twice, because a retryable failure is what the error is called.
     -------------------------------------------------------------------------
     One repeat, after a moment. More than that turns a bad minute at Supabase
     into a page that hangs, and this is somebody pressing a button rather than
     a background job that can afford to wait. */
  let attempt = await supabase.auth.mfa.enroll({ factorType: 'totp', friendlyName });

  if (attempt.error && describeAuthError(attempt.error).startsWith(MFA_SERVER_FAULT)) {
    await new Promise((resolve) => { setTimeout(resolve, 900); });
    attempt = await supabase.auth.mfa.enroll({ factorType: 'totp', friendlyName });
  }

  const { data, error } = attempt;

  if (error) throw new Error(describeAuthError(error));
  if (!data) throw new Error(describeAuthError(null));

  return {
    factorId: data.id,
    qrCode: data.totp.qr_code,
    uri: data.totp.uri,
    secret: data.totp.secret,
  };
}

/**
 * Answers a challenge with six digits.
 *
 * Used for both halves of the life of a factor — finishing an enrolment and
 * getting past the door afterwards — because Auth treats them as the same
 * operation and pretending otherwise would be two code paths that must agree.
 */
export async function verifyCode(factorId: string, code: string): Promise<void> {
  const { data: challenge, error: challengeError } =
    await supabase.auth.mfa.challenge({ factorId });

  if (challengeError) throw new Error(describeAuthError(challengeError));
  if (!challenge) throw new Error(describeAuthError(null));

  const { error } = await supabase.auth.mfa.verify({
    factorId,
    challengeId: challenge.id,
    code: code.replace(/\s+/g, ''),
  });

  if (error) throw new Error(describeAuthError(error));
}

/**
 * Takes a factor off an account.
 *
 * Auth requires an aal2 session to do this, which is the right rule: somebody
 * who has stolen a password must not be able to remove the thing standing in
 * their way. Which also means the way back for a lost phone is an
 * administrator, not this function — see manage-users.
 */
export async function removeFactor(factorId: string): Promise<void> {
  const { error } = await supabase.auth.mfa.unenroll({ factorId });
  if (error) throw new Error(describeAuthError(error));
}

/**
 * The first verified factor, which is the one a challenge is raised against.
 *
 * One is the number this product supports. A second authenticator is a second
 * thing to lose, and the recovery path is the same either way.
 */
export async function factorToChallenge(): Promise<string | null> {
  const { data } = await supabase.auth.mfa.listFactors();
  return data?.totp?.[0]?.id ?? null;
}
